import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  UserCheck, AlertTriangle, Search, Plus, X, Check, ChevronDown, ChevronUp,
  Calendar, FileText, Download, Trash2, Clock, Info
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ABSENCE_STATUSES = [
  { value: 'present', label: 'Présent', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  { value: 'absent', label: 'Absent', color: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'retard', label: 'Retard', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  { value: 'abandon', label: 'Abandon', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  { value: 'excuse', label: 'Absence justifiée', color: 'bg-blue-100 text-blue-700 border-blue-200' },
];

const SuiviAbsences = ({ sessionId, apprenants = [], modules = [] }) => {
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAbsence, setNewAbsence] = useState({ apprenant_name: '', status: 'absent', motif: '', date_absence: '', creneau: '', date_reprise: '' });

  const fetchAbsences = useCallback(async () => {
    if (!sessionId) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/absences`, { credentials: 'include' });
      if (res.ok) setAbsences(await res.json());
    } catch (e) { /* silent */ }
    setLoading(false);
  }, [sessionId]);

  useEffect(() => { fetchAbsences(); }, [fetchAbsences]);

  const handleSaveAbsence = async () => {
    if (!newAbsence.apprenant_name || !newAbsence.date_absence) {
      toast.error('Veuillez remplir l\'apprenant et la date');
      return;
    }
    const appr = apprenants.find(a => a.name === newAbsence.apprenant_name);
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/absences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...newAbsence, apprenant_email: appr?.email || '' }),
      });
      if (res.ok) {
        toast.success('Absence enregistrée');
        setShowAddForm(false);
        setNewAbsence({ apprenant_name: '', status: 'absent', motif: '', date_absence: '', creneau: '', date_reprise: '' });
        fetchAbsences();
      }
    } catch (e) { toast.error('Erreur lors de l\'enregistrement'); }
  };

  const handleDeleteAbsence = async (absenceId) => {
    if (!window.confirm('Supprimer cette absence ?')) return;
    try {
      const res = await fetch(`${API}/admin/sessions/${sessionId}/absences/${absenceId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) { toast.success('Absence supprimée'); fetchAbsences(); }
    } catch (e) { toast.error('Erreur'); }
  };

  const totalAbsent = absences.filter(a => a.status === 'absent' || a.status === 'abandon').length;
  const totalRetard = absences.filter(a => a.status === 'retard').length;
  const totalExcuse = absences.filter(a => a.status === 'excuse').length;

  const filtered = absences.filter(a => {
    const matchSearch = a.apprenant_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchStatus = filterStatus === 'all' || a.status === filterStatus;
    return matchSearch && matchStatus;
  });

  return (
    <div className="space-y-6 mt-4" data-testid="suivi-absences-content">
      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="absences-summary">
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
              <UserCheck className="h-5 w-5 text-slate-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{apprenants.length}</p>
              <p className="text-xs text-slate-500">Apprenants inscrits</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-red-200 dark:border-red-800 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{totalAbsent}</p>
              <p className="text-xs text-slate-500">Absences / Abandons</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-amber-200 dark:border-amber-800 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Clock className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{totalRetard}</p>
              <p className="text-xs text-slate-500">Retards</p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <FileText className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{totalExcuse}</p>
              <p className="text-xs text-slate-500">Absences justifiées</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions & Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Button className="bg-violet-600 hover:bg-violet-700 text-white text-xs gap-1.5 rounded-full px-5"
          onClick={() => setShowAddForm(!showAddForm)} data-testid="btn-add-absence">
          <Plus className="h-3.5 w-3.5" />{showAddForm ? 'Annuler' : 'Déclarer une absence'}
        </Button>
        <Button variant="outline" className="text-xs gap-1.5 rounded-full border-slate-300" data-testid="btn-export-absences">
          <Download className="h-3.5 w-3.5" />Exporter
        </Button>
        <div className="flex-1" />
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input className="h-9 pl-9 w-56 text-xs" placeholder="Rechercher un apprenant..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-testid="search-absences" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="h-9 w-44 text-xs" data-testid="filter-absence-status">
            <SelectValue placeholder="Tous les statuts" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {ABSENCE_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Add absence form */}
      {showAddForm && (
        <Card className="border-violet-200 dark:border-violet-800" data-testid="add-absence-form">
          <CardContent className="p-5">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Nouvelle absence</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 mb-1 block">Apprenant</label>
                <Select value={newAbsence.apprenant_name} onValueChange={v => setNewAbsence(p => ({ ...p, apprenant_name: v }))}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {apprenants.map((a, i) => <SelectItem key={i} value={a.name}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 mb-1 block">Statut</label>
                <Select value={newAbsence.status} onValueChange={v => setNewAbsence(p => ({ ...p, status: v }))}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ABSENCE_STATUSES.filter(s => s.value !== 'present').map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 mb-1 block">Date d'absence</label>
                <Input type="date" className="h-9 text-xs" value={newAbsence.date_absence}
                  onChange={e => setNewAbsence(p => ({ ...p, date_absence: e.target.value }))} data-testid="input-date-absence" />
              </div>
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 mb-1 block">Créneau horaire</label>
                <Select value={newAbsence.creneau} onValueChange={v => setNewAbsence(p => ({ ...p, creneau: v }))}>
                  <SelectTrigger className="h-9 text-xs" data-testid="input-creneau"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="matin">Matin (08:00 - 12:00)</SelectItem>
                    <SelectItem value="apres-midi">Après-midi (13:00 - 17:00)</SelectItem>
                    <SelectItem value="journee">Journée entière</SelectItem>
                    <SelectItem value="custom">Personnalisé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {newAbsence.creneau === 'custom' && (
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-xs text-slate-600 dark:text-slate-300 mb-1 block">De</label>
                    <Input type="time" className="h-9 text-xs" value={newAbsence.creneau_start || ''}
                      onChange={e => setNewAbsence(p => ({ ...p, creneau_start: e.target.value }))} data-testid="input-creneau-start" />
                  </div>
                  <div className="flex-1">
                    <label className="text-xs text-slate-600 dark:text-slate-300 mb-1 block">À</label>
                    <Input type="time" className="h-9 text-xs" value={newAbsence.creneau_end || ''}
                      onChange={e => setNewAbsence(p => ({ ...p, creneau_end: e.target.value }))} data-testid="input-creneau-end" />
                  </div>
                </div>
              )}
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 mb-1 block">Motif</label>
                <Input className="h-9 text-xs" placeholder="Motif de l'absence..." value={newAbsence.motif}
                  onChange={e => setNewAbsence(p => ({ ...p, motif: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs text-slate-600 dark:text-slate-300 mb-1 block">Date de reprise prévue</label>
                <Input type="date" className="h-9 text-xs" value={newAbsence.date_reprise}
                  onChange={e => setNewAbsence(p => ({ ...p, date_reprise: e.target.value }))} />
              </div>
              <div className="flex items-end">
                <Button className="bg-violet-600 hover:bg-violet-700 text-white text-xs h-9 px-6"
                  onClick={handleSaveAbsence} data-testid="btn-save-absence">
                  <Check className="h-3.5 w-3.5 mr-1" />Enregistrer
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Absences table */}
      <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden" data-testid="absences-table">
        <div className="grid grid-cols-12 gap-0 bg-slate-50 dark:bg-slate-800/60 text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-200 dark:border-slate-700">
          <div className="col-span-2 px-4 py-3">Apprenant</div>
          <div className="col-span-2 px-4 py-3">Date d'absence</div>
          <div className="col-span-2 px-4 py-3">Créneau</div>
          <div className="col-span-1 px-4 py-3">Statut</div>
          <div className="col-span-2 px-4 py-3">Motif</div>
          <div className="col-span-2 px-4 py-3">Reprise prévue</div>
          <div className="col-span-1 px-4 py-3">Actions</div>
        </div>

        {loading ? (
          <div className="px-6 py-8 text-center text-sm text-slate-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-8 text-center text-sm text-slate-400" data-testid="no-absences">
            <UserCheck className="h-10 w-10 mx-auto text-slate-300 mb-2" />
            <p>Aucune absence enregistrée.</p>
            <p className="text-xs mt-1">Cliquez sur "Déclarer une absence" pour commencer.</p>
          </div>
        ) : filtered.map((abs) => {
          const statusDef = ABSENCE_STATUSES.find(s => s.value === abs.status) || ABSENCE_STATUSES[1];
          return (
            <div key={abs.absence_id} className="grid grid-cols-12 gap-0 border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50/50 dark:hover:bg-slate-800/30"
              data-testid={`absence-row-${abs.absence_id}`}>
              <div className="col-span-2 px-4 py-3 flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center shrink-0">
                  <UserCheck className="h-3.5 w-3.5 text-violet-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">{abs.apprenant_name}</p>
                  <p className="text-xs text-slate-400">{abs.apprenant_email}</p>
                </div>
              </div>
              <div className="col-span-2 px-4 py-3 flex items-center">
                <span className="text-sm text-slate-700 dark:text-slate-300">
                  {abs.date_absence ? new Date(abs.date_absence).toLocaleDateString('fr-FR') : '-'}
                </span>
              </div>
              <div className="col-span-2 px-4 py-3 flex items-center">
                <span className="text-xs text-slate-600 dark:text-slate-300">
                  {abs.creneau === 'matin' ? 'Matin (08:00-12:00)'
                    : abs.creneau === 'apres-midi' ? 'Après-midi (13:00-17:00)'
                    : abs.creneau === 'journee' ? 'Journée entière'
                    : abs.creneau === 'custom' && abs.creneau_start ? `${abs.creneau_start} - ${abs.creneau_end || ''}`
                    : '-'}
                </span>
              </div>
              <div className="col-span-1 px-4 py-3 flex items-center">
                <Badge variant="outline" className={`text-xs ${statusDef.color}`}>{statusDef.label}</Badge>
              </div>
              <div className="col-span-2 px-4 py-3 flex items-center">
                <span className="text-xs text-slate-500">{abs.motif || '-'}</span>
              </div>
              <div className="col-span-2 px-4 py-3 flex items-center">
                <span className="text-xs text-slate-500">
                  {abs.date_reprise ? new Date(abs.date_reprise).toLocaleDateString('fr-FR') : '-'}
                </span>
              </div>
              <div className="col-span-1 px-4 py-3 flex items-center">
                <button onClick={() => handleDeleteAbsence(abs.absence_id)}
                  className="text-slate-400 hover:text-red-500 transition-colors" data-testid={`delete-absence-${abs.absence_id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SuiviAbsences;
